"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import Link from "next/link"
import { Check, Share2, Trophy, Zap, Leaf, CalendarDays, Timer } from "lucide-react"
import { arcadeAnalytics } from "@/lib/analytics"
import { LAWN_SPACE_BEST_SCORE_KEY } from "@/lib/lawn-space-shooter/storage"

const STRIPE_BEST_SCORE_KEY = "wgis:lawn-stripe-studio:best-score"
const BLADES_TRIMMED_KEY = "wgis:arcade:blades-trimmed"
const BLADE_COUNT = 28

const DAILY_PATTERNS = ["straight", "diagonal", "checkerboard"] as const
type DailyPattern = (typeof DAILY_PATTERNS)[number]

const PATTERN_LABELS: Record<DailyPattern, string> = {
  straight: "Straight stripes",
  diagonal: "Diagonals",
  checkerboard: "Checkerboard",
}

function readScore(key: string): number {
  try {
    const parsed = Number.parseInt(window.localStorage.getItem(key) ?? "0", 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
  } catch {
    return 0
  }
}

/** Deterministic pattern-of-the-day from the visitor's local date. */
function patternForToday(): DailyPattern {
  const now = new Date()
  const seed = now.getFullYear() * 372 + now.getMonth() * 31 + now.getDate()
  return DAILY_PATTERNS[seed % DAILY_PATTERNS.length]
}

function Chip({ children, tone = "neutral" }: { children: React.ReactNode; tone?: "neutral" | "gold" | "green" }) {
  const tones = {
    neutral: "border-white/60 bg-white/55 text-gray-700",
    gold: "border-amber-200/80 bg-amber-50/90 text-amber-800",
    green: "border-green-200/80 bg-green-50/90 text-green-800",
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${tones[tone]}`}
    >
      {children}
    </span>
  )
}

/** Animated CSS gameplay preview: drifting starfield, patrol enemies, rising shot. */
function ShooterPreview() {
  return (
    <div className="arcade-preview relative aspect-[16/10] w-full overflow-hidden rounded-2xl bg-[#0b1023]">
      <div className="arcade-stars-far absolute inset-0" aria-hidden="true" />
      <div className="arcade-stars-near absolute inset-0" aria-hidden="true" />
      <div className="arcade-enemy absolute left-[22%] top-[16%] text-xl" aria-hidden="true">🌼</div>
      <div className="arcade-enemy-b absolute left-[62%] top-[10%] text-xl" aria-hidden="true">🌾</div>
      <div className="arcade-shot absolute bottom-[26%] left-1/2 h-4 w-1 -translate-x-1/2 rounded-full bg-amber-300" aria-hidden="true" />
      <svg
        className="arcade-ship absolute bottom-[10%] left-1/2 h-8 w-8 -translate-x-1/2"
        viewBox="0 0 32 32"
        aria-hidden="true"
      >
        <path d="M16 2 L26 26 L16 20 L6 26 Z" fill="#4ade80" stroke="#bbf7d0" strokeWidth="1.5" />
      </svg>
      <div className="absolute inset-x-0 bottom-0 h-2 bg-green-500/70" aria-hidden="true" />
    </div>
  )
}

/** Animated CSS gameplay preview: stripes reveal themselves as the mower sweeps. */
function StripePreview() {
  return (
    <div className="arcade-preview relative aspect-[16/10] w-full overflow-hidden rounded-2xl bg-[#79b851]">
      <div className="arcade-stripes absolute inset-x-0 top-0" aria-hidden="true" />
      <div className="arcade-mower absolute h-5 w-8 rounded-md bg-amber-400 shadow-md" aria-hidden="true">
        <div className="absolute -bottom-1 left-1 h-2 w-2 rounded-full bg-gray-800" />
        <div className="absolute -bottom-1 right-1 h-2 w-2 rounded-full bg-gray-800" />
      </div>
    </div>
  )
}

type ArcadeCardProps = {
  href: string
  onClick: () => void
  kicker: string
  kickerIcon: React.ReactNode
  title: string
  titleClass: string
  preview: React.ReactNode
  chips: React.ReactNode
}

function ArcadeCard({ href, onClick, kicker, kickerIcon, title, titleClass, preview, chips }: ArcadeCardProps) {
  return (
    <Link
      href={href}
      prefetch={false}
      onClick={onClick}
      className="group ds-glass flex flex-col gap-4 rounded-3xl p-5 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 md:p-6"
    >
      <span className="inline-flex items-center gap-1.5 self-start rounded-full border border-white/60 bg-white/55 px-3 py-1 text-xs font-bold uppercase tracking-wider text-gray-700">
        {kickerIcon}
        {kicker}
      </span>
      {preview}
      <div className="flex items-center justify-between gap-3">
        <span className={`font-poppins text-xl font-bold ${titleClass}`}>{title}</span>
        <span className="inline-flex items-center rounded-full bg-green-700 px-4 py-1.5 text-sm font-semibold text-white transition-colors group-hover:bg-green-800">
          Play
        </span>
      </div>
      <div className="flex flex-wrap gap-2">{chips}</div>
    </Link>
  )
}

/** neal.fun-style living element: a strip of grass you mow with the pointer. */
function MowableStrip() {
  const [mown, setMown] = useState<boolean[]>(() => Array(BLADE_COUNT).fill(false))
  const [totalTrimmed, setTotalTrimmed] = useState(0)
  const regrowTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setTotalTrimmed(readScore(BLADES_TRIMMED_KEY))
    return () => {
      if (regrowTimer.current) clearTimeout(regrowTimer.current)
    }
  }, [])

  const mowBlade = useCallback((index: number) => {
    setMown((prev) => {
      if (prev[index]) return prev
      const next = [...prev]
      next[index] = true
      setTotalTrimmed((count) => {
        const total = count + 1
        try {
          window.localStorage.setItem(BLADES_TRIMMED_KEY, String(total))
        } catch {
          /* per-visitor whimsy only */
        }
        return total
      })
      if (next.every(Boolean)) {
        arcadeAnalytics.grassStripMowed(readScore(BLADES_TRIMMED_KEY) + 1)
        if (regrowTimer.current) clearTimeout(regrowTimer.current)
        regrowTimer.current = setTimeout(() => setMown(Array(BLADE_COUNT).fill(false)), 2500)
      }
      return next
    })
  }, [])

  return (
    <div className="ds-glass rounded-3xl px-5 py-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-green-900">Warm up: mow this.</p>
        <p className="text-xs font-medium text-green-800" aria-live="polite">
          {totalTrimmed.toLocaleString()} blades trimmed
        </p>
      </div>
      <div className="mt-3 flex h-10 items-end gap-1" role="presentation">
        {mown.map((isMown, index) => (
          <button
            key={index}
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onPointerEnter={() => mowBlade(index)}
            onClick={() => mowBlade(index)}
            className={`min-w-0 flex-1 origin-bottom rounded-t-full transition-all duration-200 ${
              isMown ? "h-2 bg-lime-600/80" : "h-8 bg-green-600 hover:bg-green-500"
            } ${index % 3 === 1 ? "md:h-9" : ""} ${isMown ? "md:h-2" : ""}`}
          />
        ))}
      </div>
    </div>
  )
}

export function LawnArcade() {
  const [shooterBest, setShooterBest] = useState(0)
  const [stripeBest, setStripeBest] = useState(0)
  const [dailyPattern, setDailyPattern] = useState<DailyPattern | null>(null)
  const [shareState, setShareState] = useState<"idle" | "copied">("idle")

  useEffect(() => {
    setShooterBest(readScore(LAWN_SPACE_BEST_SCORE_KEY))
    setStripeBest(readScore(STRIPE_BEST_SCORE_KEY))
    setDailyPattern(patternForToday())
  }, [])

  const shareChallenge = useCallback(async () => {
    const scoreLine =
      shooterBest > 0 || stripeBest > 0
        ? ` My best: ${Math.max(shooterBest, stripeBest).toLocaleString()}.`
        : ""
    const text = `Think you can out-mow me? Free lawn games at whatgrassisthis.com.${scoreLine}`
    const url = "https://whatgrassisthis.com/games"
    try {
      if (navigator.share) {
        await navigator.share({ title: "The Lawn Arcade", text, url })
        arcadeAnalytics.challengeShared("web-share")
        return
      }
    } catch {
      return // user dismissed the share sheet
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`)
      setShareState("copied")
      arcadeAnalytics.challengeShared("clipboard")
      setTimeout(() => setShareState("idle"), 2000)
    } catch {
      /* clipboard unavailable; the button simply does nothing */
    }
  }, [shooterBest, stripeBest])

  const dailyHref = dailyPattern
    ? `/games/lawn-stripe-studio?pattern=${dailyPattern}&mode=challenge&source=arcade-daily`
    : "/games/lawn-stripe-studio?source=arcade"

  return (
    <div className="flex flex-col gap-5">
      <style>{ARCADE_CSS}</style>

      <div className="grid gap-5 md:grid-cols-2">
        <ArcadeCard
          href="/games/lawn-space-shooter?source=arcade"
          onClick={() => arcadeAnalytics.gameCardClicked("lawn-space-shooter", shooterBest > 0)}
          kicker="Need a thrill?"
          kickerIcon={<Zap className="h-3.5 w-3.5 text-amber-500" />}
          title="Lawn Force One"
          titleClass="text-indigo-950"
          preview={<ShooterPreview />}
          chips={
            <>
              {shooterBest > 0 ? (
                <Chip tone="gold">
                  <Trophy className="h-3.5 w-3.5" />
                  Best {shooterBest.toLocaleString()}
                </Chip>
              ) : (
                <Chip tone="gold">
                  <Trophy className="h-3.5 w-3.5" />
                  Set your first score
                </Chip>
              )}
              <Chip>
                <Timer className="h-3.5 w-3.5" />
                Two-minute blast
              </Chip>
            </>
          }
        />

        <ArcadeCard
          href={dailyHref}
          onClick={() =>
            arcadeAnalytics.gameCardClicked("lawn-stripe-studio", stripeBest > 0, dailyPattern ?? undefined)
          }
          kicker="Need to unwind?"
          kickerIcon={<Leaf className="h-3.5 w-3.5 text-green-600" />}
          title="Lawn Stripe Studio"
          titleClass="text-green-900"
          preview={<StripePreview />}
          chips={
            <>
              {stripeBest > 0 ? (
                <Chip tone="green">
                  <Trophy className="h-3.5 w-3.5" />
                  Best {stripeBest.toLocaleString()}
                </Chip>
              ) : (
                <Chip tone="green">
                  <Trophy className="h-3.5 w-3.5" />
                  Mow your first lawn
                </Chip>
              )}
              {dailyPattern ? (
                <Chip tone="neutral">
                  <CalendarDays className="h-3.5 w-3.5" />
                  Today&apos;s pattern: {PATTERN_LABELS[dailyPattern]}
                </Chip>
              ) : null}
            </>
          }
        />
      </div>

      <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
        <p className="text-sm text-gray-700">Free. No download. Scores save on this device.</p>
        <button
          type="button"
          onClick={shareChallenge}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-white/70 bg-white/60 px-5 text-sm font-semibold text-green-800 transition-colors hover:bg-white/85"
        >
          {shareState === "copied" ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
          {shareState === "copied" ? "Link copied" : "Challenge a friend"}
        </button>
      </div>

      <MowableStrip />
    </div>
  )
}

const ARCADE_CSS = `
.arcade-stars-far, .arcade-stars-near {
  background-image: radial-gradient(1px 1px at 20% 15%, #cbd5f5 100%, transparent 100%),
    radial-gradient(1px 1px at 65% 40%, #94a3d8 100%, transparent 100%),
    radial-gradient(1.5px 1.5px at 40% 75%, #e2e8ff 100%, transparent 100%),
    radial-gradient(1px 1px at 85% 20%, #cbd5f5 100%, transparent 100%);
  background-size: 180px 140px;
  animation: arcade-drift 9s linear infinite;
}
.arcade-stars-near {
  background-size: 120px 100px;
  opacity: 0.9;
  animation-duration: 5s;
}
@keyframes arcade-drift {
  from { background-position-y: 0; }
  to { background-position-y: 140px; }
}
.arcade-ship { animation: arcade-hover 2.4s ease-in-out infinite; }
@keyframes arcade-hover {
  0%, 100% { transform: translateX(-140%); }
  50% { transform: translateX(40%); }
}
.arcade-shot { animation: arcade-shot 1.2s linear infinite; }
@keyframes arcade-shot {
  from { transform: translate(-50%, 0); opacity: 1; }
  to { transform: translate(-50%, -110px); opacity: 0; }
}
.arcade-enemy { animation: arcade-enemy 3.5s ease-in-out infinite alternate; }
.arcade-enemy-b { animation: arcade-enemy 2.8s ease-in-out infinite alternate-reverse; }
@keyframes arcade-enemy {
  from { transform: translateX(0); }
  to { transform: translateX(46px); }
}
.arcade-stripes {
  background: repeating-linear-gradient(180deg, #8ecf62 0 14%, #5f9c3e 14% 28%);
  height: 0;
  animation: arcade-reveal 8s linear infinite;
}
@keyframes arcade-reveal {
  0% { height: 0; }
  92% { height: 100%; }
  100% { height: 100%; }
}
.arcade-mower {
  animation: arcade-mow-x 1.6s ease-in-out infinite alternate, arcade-mow-y 8s steps(5, jump-start) infinite;
}
@keyframes arcade-mow-x {
  from { left: 4%; }
  to { left: calc(96% - 2rem); }
}
@keyframes arcade-mow-y {
  from { top: -4%; }
  to { top: 96%; }
}
@media (prefers-reduced-motion: reduce) {
  .arcade-preview *, .arcade-preview { animation: none !important; }
  .arcade-stripes { height: 70%; }
  .arcade-mower { left: 40%; top: 62%; }
}
`
